/**
 * Bridges the position service to the live-sharing API (spec 15). Subscribes
 * to posStore like recorderService, but is entirely independent of the
 * recorder — sharing without logging a tur is fine, and vice versa.
 *
 * Sampling: fixes normally arrive ~1 Hz, far more granular than a "peace of
 * mind" viewer refreshing every 15 s needs. We buffer at most one point per
 * SAMPLE_INTERVAL_MS so the offline cap (240 points) really does represent
 * ~4 h, and a live session sends a trickle of traffic, not a stream.
 */
import { createStore } from 'zustand/vanilla';
import { SHARE_POINTS_MAX_PER_CALL } from '@boatnav/schemas';
import { posStore, type Fix } from '../nav/posStore.js';
import { api, ApiError } from '../../lib/apiClient.js';
import {
  getShareSession,
  putShareSession,
  clearShareSession,
  getShareBuffer,
  putShareBuffer,
  clearShareBuffer,
  type ShareBufferPoint,
} from '../../lib/db.js';

export const SAMPLE_INTERVAL_MS = 60_000;
export const FLUSH_INTERVAL_MS = 60_000;
export const FLUSH_POINT_TRIGGER = 20;
export const BUFFER_CAP_POINTS = 240; // ~4h at one sample/minute

export interface ShareUiState {
  active: boolean;
  id: string | null;
  expiresAt: number | null;
  boatName: string | null;
}

export const shareStore = createStore<ShareUiState>(() => ({
  active: false,
  id: null,
  expiresAt: null,
  boatName: null,
}));

interface ActiveSession {
  id: string;
  writeKey: string;
  expiresAt: number;
  boatName: string | null;
}

let session: ActiveSession | null = null;
let buffer: ShareBufferPoint[] = [];
let lastSampledAt = 0;
let flushing = false;
let started = false;
let unsubPos: (() => void) | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

export function shareUrl(id: string): string {
  return `${location.origin}/del/${id}`;
}

function syncUiStore(): void {
  shareStore.setState({
    active: session != null,
    id: session?.id ?? null,
    expiresAt: session?.expiresAt ?? null,
    boatName: session?.boatName ?? null,
  });
}

function toBufferPoint(fix: Fix): ShareBufferPoint {
  return { lat: fix.lat, lon: fix.lon, ts: fix.ts, sogKn: fix.sogKn, cogDeg: fix.cogDeg };
}

function clearTimers(): void {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = null;
}

function armTimers(): void {
  clearTimers();
  flushTimer = setInterval(() => void flushNow(), FLUSH_INTERVAL_MS);
  if (session) {
    // setTimeout's delay is a 32-bit signed int (~24.8 days) — TTL maxes at
    // 7 days (spec 15) so this always fits.
    const delay = Math.max(0, session.expiresAt - Date.now());
    expiryTimer = setTimeout(() => void handleLocalExpiry(), delay);
  }
}

/** The server independently enforces expiry; this just keeps the DELER chip
 *  and local state honest without waiting for the next failed flush. */
async function handleLocalExpiry(): Promise<void> {
  session = null;
  buffer = [];
  clearTimers();
  await clearShareSession();
  await clearShareBuffer();
  syncUiStore();
}

async function persistBuffer(): Promise<void> {
  await putShareBuffer(buffer);
}

function onFix(fix: Fix): void {
  if (!session) return;
  if (lastSampledAt !== 0 && fix.ts - lastSampledAt < SAMPLE_INTERVAL_MS) return;
  lastSampledAt = fix.ts;

  buffer.push(toBufferPoint(fix));
  if (buffer.length > BUFFER_CAP_POINTS) {
    buffer = buffer.slice(buffer.length - BUFFER_CAP_POINTS);
  }
  void persistBuffer();

  if (buffer.length >= FLUSH_POINT_TRIGGER) void flushNow();
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Sends buffered points in batches of <= SHARE_POINTS_MAX_PER_CALL. Safe to
 *  call repeatedly (timer, reconnect, manual) — no-ops with nothing to send
 *  or a flush already in flight. */
export async function flushNow(): Promise<void> {
  if (!session || buffer.length === 0 || flushing || !isOnline()) return;
  flushing = true;
  try {
    while (session && buffer.length > 0) {
      const chunk = buffer.slice(0, SHARE_POINTS_MAX_PER_CALL);
      try {
        await api.sharePoints(session.id, session.writeKey, chunk);
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 410)) {
          // The session died server-side (revoked/expired) — stop locally too.
          await handleLocalExpiry();
        }
        return; // network error or dead session: stop, retry next trigger
      }
      buffer = buffer.slice(chunk.length);
      await persistBuffer();
    }
  } finally {
    flushing = false;
  }
}

/** Called from the app-wide `online` listener (wired in initShareService). */
export function notifyOnline(): void {
  void flushNow();
}

/** Resume a session left in IDB (reload/restart) — recorder-independent, runs
 *  app-wide (spec 15). Safe to call more than once. */
export async function initShareService(): Promise<void> {
  if (started) return;
  started = true;

  const rec = await getShareSession();
  if (rec) {
    if (rec.expiresAt <= Date.now()) {
      await clearShareSession();
      await clearShareBuffer();
    } else {
      session = { id: rec.sessionId, writeKey: rec.writeKey, expiresAt: rec.expiresAt, boatName: rec.boatName };
      buffer = await getShareBuffer();
      armTimers();
    }
  }
  syncUiStore();

  unsubPos = posStore.subscribe((s, prev) => {
    if (s.fix !== prev.fix && s.fix) onFix(s.fix);
  });
  if (typeof window !== 'undefined') window.addEventListener('online', notifyOnline);
}

/** Tears down listeners/timers AND resets in-memory state (test/hygiene hook
 *  — production never calls this; sharing is meant to run app-wide). */
export function stopShareService(): void {
  unsubPos?.();
  unsubPos = null;
  clearTimers();
  if (typeof window !== 'undefined') window.removeEventListener('online', notifyOnline);
  started = false;
  session = null;
  buffer = [];
  lastSampledAt = 0;
  flushing = false;
}

/** Start sharing (spec 15: "Del turen"). Creates the server session, persists
 *  the writeKey locally, and arms buffering/flush timers. */
export async function startShare(boatName?: string, ttlHours?: number): Promise<{ url: string; expiresAt: number }> {
  const res = await api.shareCreate(boatName, ttlHours);
  session = { id: res.id, writeKey: res.writeKey, expiresAt: res.expiresAt, boatName: boatName ?? null };
  buffer = [];
  lastSampledAt = 0;

  await putShareSession({
    sessionId: session.id,
    writeKey: session.writeKey,
    expiresAt: session.expiresAt,
    boatName: session.boatName,
    createdAt: Date.now(),
  });
  await clearShareBuffer();
  armTimers();
  syncUiStore();

  // Seed immediately so the viewer isn't blank for a full sample interval.
  const current = posStore.getState().fix;
  if (current) onFix(current);

  return { url: shareUrl(session.id), expiresAt: session.expiresAt };
}

/** Stop sharing (spec 15: "Stopp deling"). Best-effort server call — local
 *  state is always cleared, even if the network request fails, so the DELER
 *  chip and privacy contract ("posisjonene slettes") never lie to the user. */
export async function stopShare(): Promise<void> {
  const current = session;
  session = null;
  buffer = [];
  clearTimers();
  await clearShareSession();
  await clearShareBuffer();
  syncUiStore();

  if (current) {
    try {
      await api.shareEnd(current.id, current.writeKey);
    } catch {
      // Best-effort: the server also auto-expires, so this can't leak forever.
    }
  }
}
